import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
});

// Request interceptor — attach JWT if present
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('fleet_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Upload FormData via native fetch instead of axios.
 * Axios sets Content-Type: application/json by default (even for POST with
 * FormData) and the browser cannot override it, so multer never sees the
 * multipart boundary. fetch() leaves Content-Type unset for FormData, letting
 * the browser add the correct multipart/form-data; boundary=... header.
 */
async function uploadFormData(method, path, fd) {
  const token = localStorage.getItem('fleet_token');
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  // Content-Type intentionally omitted — browser sets multipart with boundary

  const url = `${API_BASE}${path}`;
  const res = await fetch(url, { method, body: fd, headers });

  if (res.status === 401) {
    localStorage.removeItem('fleet_token');
    window.location.href = '/login';
    return;
  }

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.message || `HTTP ${res.status}`);
    err.response = { data: json, status: res.status };
    throw err;
  }
  return { data: json };
}

// Response interceptor — redirect to login on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('fleet_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const apiEndpoints = {
  // Auth
  auth: {
    login:          (data) => api.post('/auth/login', data),
    driverLogin:    (data) => api.post('/auth/driver-login', data),
    changePassword: (data) => api.patch('/auth/change-password', data),
    logout:         () => api.post('/auth/logout'),
  },

  // Activity log — admin only
  activityLog: {
    list: (params) => api.get('/activity-log', { params }),
  },

  // Dashboard
  dashboard: {
    summary: () => api.get('/dashboard/summary'),
  },

  // Vehicles
  vehicles: {
    list:            (params)    => api.get('/vehicles', { params }),
    get:             (id)        => api.get(`/vehicles/${id}`),
    create:          (data)      => api.post('/vehicles', data),
    update:          (id, data)  => api.patch(`/vehicles/${id}`, data),
    delete:          (id)        => api.delete(`/vehicles/${id}`),
    deleteDocType:   (id, docType) => api.delete(`/vehicles/${id}/docs/${docType}`),
    subAssign:       (id, data)  => api.post(`/vehicles/${id}/sub-assign`, data),
    subDeallocate:   (id, data)  => api.post(`/vehicles/${id}/sub-deallocate`, data),
  },

  // Drivers
  drivers: {
    list:           () => api.get('/drivers'),
    create:         (data) => api.post('/drivers', data),
    update:         (id, data) => api.patch(`/drivers/${id}`, data),
    delete:         (id) => api.delete(`/drivers/${id}`),
    setCredentials: (id, data) => api.post(`/drivers/${id}/credentials`, data),
  },

  // Companies
  companies: {
    list: () => api.get('/companies'),
    create: (data) => api.post('/companies', data),
    update: (key, data) => api.patch(`/companies/${key}`, data),
    delete: (key) => api.delete(`/companies/${key}`),
  },

  // Events
  events: {
    list: () => api.get('/events'),
    create: (data) => api.post('/events', data),
    update: (id, data) => api.patch(`/events/${id}`, data),
    delete: (id) => api.delete(`/events/${id}`),
  },

  // Documents
  documents: {
    list: () => api.get('/documents'),
    create: (data, files) => {
      const fileList = Array.isArray(files) ? files.filter(Boolean) : (files ? [files] : []);
      if (fileList.length) {
        const fd = new FormData();
        Object.entries(data).forEach(([k, v]) => { if (v != null && v !== '') fd.append(k, String(v)); });
        fileList.forEach((f) => fd.append('files', f));
        return uploadFormData('POST', '/documents', fd);
      }
      return api.post('/documents', data);
    },
    delete: (id) => api.delete(`/documents/${id}`),
  },

  // Users
  users: {
    list: () => api.get('/users'),
  },

  // Assignments
  assignments: {
    create: (data) => api.post('/assignments', data),
  },

  // Accidents
  accidents: {
    list: () => api.get('/accidents'),
    create: (data, files) => {
      const fileList = Array.isArray(files) ? files.filter(Boolean) : (files ? [files] : []);
      if (fileList.length) {
        const fd = new FormData();
        Object.entries(data).forEach(([k, v]) => { if (v != null && v !== '') fd.append(k, String(v)); });
        fileList.forEach((f) => fd.append('attachments', f));
        return uploadFormData('POST', '/accidents', fd);
      }
      return api.post('/accidents', data);
    },
    update: (id, data, files) => {
      const fileList = Array.isArray(files) ? files.filter(Boolean) : (files ? [files] : []);
      if (fileList.length) {
        const fd = new FormData();
        Object.entries(data).forEach(([k, v]) => { if (v != null && v !== '') fd.append(k, String(v)); });
        fileList.forEach((f) => fd.append('attachments', f));
        return uploadFormData('PATCH', `/accidents/${id}`, fd);
      }
      return api.patch(`/accidents/${id}`, data);
    },
    delete: (id) => api.delete(`/accidents/${id}`),
  },

  // Costs
  costs: {
    list: () => api.get('/costs'),
    create: (data, files) => {
      const fileList = Array.isArray(files) ? files.filter(Boolean) : (files ? [files] : []);
      if (fileList.length) {
        const fd = new FormData();
        Object.entries(data).forEach(([k, v]) => { if (v != null && v !== '') fd.append(k, String(v)); });
        fileList.forEach((f) => fd.append('attachments', f));
        return uploadFormData('POST', '/costs', fd);
      }
      return api.post('/costs', data);
    },
    update: (id, data, files) => {
      const fileList = Array.isArray(files) ? files.filter(Boolean) : (files ? [files] : []);
      if (fileList.length) {
        const fd = new FormData();
        Object.entries(data).forEach(([k, v]) => { if (v != null && v !== '') fd.append(k, String(v)); });
        fileList.forEach((f) => fd.append('attachments', f));
        return uploadFormData('PATCH', `/costs/${id}`, fd);
      }
      return api.patch(`/costs/${id}`, data);
    },
    delete: (id) => api.delete(`/costs/${id}`),
  },

  // Leasing
  leasing: {
    list: () => api.get('/leasing'),
    create: (data, files) => {
      const fileList = Array.isArray(files) ? files.filter(Boolean) : (files ? [files] : []);
      if (fileList.length) {
        const fd = new FormData();
        Object.entries(data).forEach(([k, v]) => v != null && fd.append(k, String(v)));
        fileList.forEach((f) => fd.append('files', f));
        return uploadFormData('POST', '/leasing', fd);
      }
      return api.post('/leasing', data);
    },
    update: (id, data, files) => {
      const fileList = Array.isArray(files) ? files.filter(Boolean) : (files ? [files] : []);
      if (fileList.length) {
        const fd = new FormData();
        Object.entries(data).forEach(([k, v]) => v != null && fd.append(k, String(v)));
        fileList.forEach((f) => fd.append('files', f));
        return uploadFormData('PATCH', `/leasing/${id}`, fd);
      }
      return api.patch(`/leasing/${id}`, data);
    },
    delete: (id) => api.delete(`/leasing/${id}`),
    markPayment: (id, index, data) => api.patch(`/leasing/${id}/payment/${index}`, data),
  },

  // Suppliers
  suppliers: {
    list:   ()          => api.get('/suppliers'),
    create: (data)      => api.post('/suppliers', data),
    update: (id, data)  => api.patch(`/suppliers/${id}`, data),
    delete: (id)        => api.delete(`/suppliers/${id}`),
  },

  // Reminders
  reminders: {
    list:      (params) => api.get('/reminders', { params }),
    create:    (data)   => api.post('/reminders', data),
    markRead:  (id)     => api.patch(`/reminders/${id}/read`),
  },

  // Inspections
  inspections: {
    list: (params) => api.get('/inspections', { params }),
    create: (data, files) => {
      const fd = new FormData();
      Object.entries(data).forEach(([k, v]) => v != null && fd.append(k, String(v)));
      (files || []).forEach((f) => fd.append('files', f));
      return uploadFormData('POST', '/inspections', fd);
    },
    delete: (id) => api.delete(`/inspections/${id}`),
  },

  // Device tokens (push notifications)
  deviceTokens: {
    save: (data) => api.post('/device-token', data),
  },

  // Push notifications — admin only
  notifications: {
    status:     () => api.get('/notifications/status'),
    sendAlerts: () => api.post('/notifications/send-alerts'),
  },

  // Health check
  health: () => api.get('/health'),
};

export default api;
